import React, { useEffect, useMemo, useState } from 'react';
import { Col, Form, Input, Row } from 'antd';
import PrimaryButton from '../../../../components/generic/buttons/PrimaryButton';
import { executeIntent } from '../../../utils/IntentExecutor';
import IntentType from '../../../../intentexecutor/utils/IntentType';
import { useGetOrgIdentifier } from '../../../generic/hooks/useGetOrgIdentifier';
import { notifiyUser } from '../../../utils/Notification';
import { strings } from '../../../utils/strings';
import "../profilepage.css";
import SecondaryButton from '../../../../components/generic/buttons/SecondaryButton';
import { publishEvent } from '../../../../components/generic/EventPublisher';
import { useSetRecoilState } from 'recoil';
import { currentUserTimeout } from '../../../../components/recoil/userTimeoutRecoil';
import { performPostRequest } from '../../../../service/network/NetworkService';
import { getApiUrl } from '../../../../service/network/UrlMapper';
import ErrorNotificationComponent from '../../../../components/generic/error/ErrorNotificationComponent';
import { useReadMemory } from '../../../generic/hooks/useReadMemory';
import MemoryType from '../../../../service/memory/enum/MemoryType';

const SetDashboardDataValuesComponent = () => {
  const [wagePerHour, setWagePerHour] = useState(0);
  const [mileage, setMileage] = useState(0);
  const [targetHours, setTargetHours] = useState(0);
  const [timeout, setTimeout] = useState(0);
  const [lockTimeout, setLockTimeout] = useState(0);
  const [form] = Form.useForm();
  const organizationIdentifier = useGetOrgIdentifier();
  const [formDisable, setFormDisable] = useState(true);
  const setUserTimeout = useSetRecoilState(currentUserTimeout);
  const [refresh, setRefresh] = useState(false)
  const userDetails = useReadMemory(MemoryType.USER);

  function compareAndExtractChanges(newValues, oldValues) {
    const previousValues = {};
    const updatedValues = {};

    for (let key in newValues) {
      const newValue = Number(newValues[key]);

      if (newValue !== oldValues[key]) {
        previousValues[key] = oldValues[key];
        updatedValues[key] = newValue;
      }
    }

    return { previousValues, updatedValues };
  }

  const onFinish = async (values) => {
    const organizationSettings = {
      TARGET_HOURS: targetHours,
      WAGE_PER_HOUR: wagePerHour,
      MILEAGE: mileage,
      TIMEOUT_IN_MINUTE: timeout
    }
    const { previousValues, updatedValues } = compareAndExtractChanges(values, organizationSettings)

    if (Object.keys(previousValues).length && Object.keys(updatedValues).length) {
      const newValues = Object.keys(values).map(item => ({ key: item, value: Number(values[item]) }));
      const payload = {
        configurationKey: "niramaya.dashboarddata.keys",
        configurationValue: JSON.stringify(newValues),
      };
      const result = await executeIntent(IntentType.ADD_CONFIGURATION, payload, {
        organizationIdentifier: organizationIdentifier,
      });

      setUserTimeout({
        LOCK_TIMEOUT_IN_MINUTE: values.LOCK_TIMEOUT_IN_MINUTE,
        TIMEOUT_IN_MINUTE: values.TIMEOUT_IN_MINUTE,
      });
      publishEvent("niramaya.organization.settings.updated", userDetails, updatedValues, previousValues)
      notifiyUser(result, strings.ADD_DASHBOARD_DATA_SUCCESS, strings.ADD_DASHBOARD_DATA_ERROR);
      setFormDisable(true);
      setRefresh(prev => !prev)
    }
  };

  const formDataAndValues = useMemo(() => {
    return (
      [
        {
          label: strings.TARGET_HOURS,
          name: "TARGET_HOURS",
          initialValue: targetHours || 0,
          value: targetHours,
        },
        {
          label: strings.WAGE_PER_HOUR,
          name: "WAGE_PER_HOUR",
          initialValue: wagePerHour || 0,
          value: wagePerHour,
        },
        {
          label: strings.MILEAGE,
          name: "MILEAGE",
          initialValue: mileage || 0,
          value: mileage,
        },
        {
          label: strings.TIMEOUT_MINUTES,
          name: "TIMEOUT_IN_MINUTE",
          initialValue: timeout || 0,
          value: timeout,
        },
        {
          label: strings.LOCK_TIMEOUT_MINUTES,
          name: "LOCK_TIMEOUT_IN_MINUTE",
          initialValue: lockTimeout || 0,
          value: lockTimeout,
        },
      ]
    )
  }, [targetHours, wagePerHour, mileage, timeout, lockTimeout]);

  useEffect(() => {
    form.setFieldsValue({
      TARGET_HOURS: targetHours,
      WAGE_PER_HOUR: wagePerHour,
      MILEAGE: mileage,
      TIMEOUT_IN_MINUTE: timeout,
      LOCK_TIMEOUT_IN_MINUTE: lockTimeout,
    });
  }, [targetHours, wagePerHour, mileage, timeout, form, lockTimeout]);

  useEffect(() => {
    const getDashboardGoalsData = async () => {
      const payload = {
        intent: "get_configuration",
        payload: {
          configurationKey: ["niramaya.dashboarddata.keys"],
        },
        context: {
          organizationIdentifier: organizationIdentifier
        }
      };

      try {
        const response = await performPostRequest(getApiUrl()[IntentType.GET_CONFIGURATION], JSON.stringify(payload));
        const result = await response.json();

        const dashboardData = JSON.parse(result[0]?.configurationValue || "[]");

        const handlers = {
          "TARGET_HOURS": setTargetHours,
          "WAGE_PER_HOUR": setWagePerHour,
          "MILEAGE": setMileage,
          "TIMEOUT_IN_MINUTE": setTimeout,
          "LOCK_TIMEOUT_IN_MINUTE": setLockTimeout
        };

        for (const { key, value } of dashboardData || []) {
          const handler = handlers[key];
          if (handler) {
            handler(value);
          }
        }
      } catch (error) {
        ErrorNotificationComponent(error);
      }
    }

    getDashboardGoalsData();
  }, [organizationIdentifier, refresh]);

  return (
    <div className='dashboard-value-container'>
      <Form form={form} onFinish={onFinish} layout='vertical' labelWrap={true} name="wrap" wrapperCol={{ flex: 1 }}>
        <Row style={{ width: "100%" }} gutter={[12, 12]}>
          {formDataAndValues?.map(formItem => {
            const customRules =
            formItem.name === "LOCK_TIMEOUT_IN_MINUTE"
              ? [
                  {
                    validator: (_, value) => {
                      const timeoutInMinutes = form.getFieldValue("TIMEOUT_IN_MINUTE");
                      if (value === undefined || value < timeoutInMinutes) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("Timeout Minutes must be greater than Lock Timeout")
                      );
                    },
                  },
                ]
              : [];
            return (
              <Col xs={24} sm={12} md={8} xl={6} key={formItem?.name}>
                <Form.Item label={formItem?.label} name={formItem?.name} initialValue={formItem?.initialValue} rules={customRules} dependencies={formItem.name === "TIMEOUT_IN_MINUTE" ? ["LOCK_TIMEOUT_IN_MINUTE"] : []}>
                  <Input disabled={formDisable} className='dashboard-value-input-field' type='number'/>
                </Form.Item>
              </Col>
            )
          })}
          <Col xs={24} sm={12} md={8} xl={18} className='dashboard-value-form-btns-container'>
            {!formDisable ? (
              <Form.Item className='dashboard-value-form-btns'>
                <PrimaryButton buttonText="Save" htmlType="submit"></PrimaryButton>
                <SecondaryButton buttonText="Cancel" handleOnClick={() => setFormDisable(true)}></SecondaryButton>
              </Form.Item>
            ) : (
              <div className='dashboard-value-form-btns'>
                <PrimaryButton buttonText="Edit" handleOnClick={() => setFormDisable(false)}></PrimaryButton>
              </div>
            )}
          </Col>
        </Row>
      </Form>
    </div>
  )
}

export default SetDashboardDataValuesComponent